import React, { FC, ReactElement, useCallback, useMemo } from 'react'
import { DeviceModal } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { getAgentList } from '../../Common/API/ItemsGetters'
import { DataTableColumn } from '../../../ReactRouting/Components/Molecules/DataTable/DataTable'
import { AGENT_ADMIN_LIST_FIELDS } from '../../../NonReact/constants/ApiConstants'
import { AgentListItem, AgentListResponse } from '../../../NonReact/Types/AgentListTypes'
import { AgentMemberRow } from '../Types/Types'
import { MemberTable, MemberSearchOption } from './MemberTable'
import { EMPTY_STRING } from '../../Common/Constants/Separators'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import type { RangeEntryWrapper } from '../../Common/Components/Selects/RangeCombo/Types'
import { buildRangeFilterExpression } from '../Utils/buildRangeFilterExpression'

const INITIAL_MEMBER_OPTIONS_LIMIT: number = 50
const SEARCH_MEMBER_OPTIONS_LIMIT: number = 100
const RANGE_SEARCH_PATTERN: RegExp = /^\d*[xX]?\d*-\d*[xX]?\d*$/

type Props = {
    viewOnly?: boolean
    filterTelSysID: number
    filterPartitionID: number
    selectedRows: AgentMemberRow[]
    onRowsChange: (rows: AgentMemberRow[]) => void
    isRouterMode?: boolean
}

/**
 * Formats an ACD agent list row for the async member search options.
 *
 * @param row - ACD agent list item.
 * @returns Single-line label text.
 */
const formatAgentOptionLabel = (row: AgentListItem): string => {
    const agentNumber: string = row.AgentNumber ?? EMPTY_STRING
    const description: string = row.Description?.trim() ?? EMPTY_STRING

    return isStringNotEmpty(description) ? `${agentNumber} - ${description}` : agentNumber
}

/**
 * Agent Members tab for ACD group devices using a paginated table plus async add-search control.
 *
 * @param props - Tab props including TelSys / partition scope and current member rows.
 * @returns Agent-members management UI.
 */
export const AgentMembersTab: FC<Props> = ({
    viewOnly,
    filterTelSysID,
    filterPartitionID,
    selectedRows,
    onRowsChange,
    isRouterMode
}: Props): ReactElement => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()

    const columns: Array<DataTableColumn<AgentMemberRow, keyof AgentMemberRow>> = useMemo(
        (): Array<DataTableColumn<AgentMemberRow, keyof AgentMemberRow>> => [
            {
                field: 'AgentNumber',
                key: 'AgentNumber',
                headerName: language.get(DeviceModal.DeviceModal_Members_AgentNumber)
            },
            {
                field: 'Description',
                key: 'Description',
                headerName: language.get(DeviceModal.DeviceModal_Members_Description)
            }
        ],
        [language]
    )

    const loadOptions = useCallback(
        async (searchQuery: string): Promise<Array<MemberSearchOption<AgentListItem>>> => {
            const isInitialOpenQuery: boolean = !isStringNotEmpty(searchQuery)

            if (!isInitialOpenQuery && searchQuery.length < 2) return []

            const isRangeQuery: boolean = !isInitialOpenQuery && RANGE_SEARCH_PATTERN.test(searchQuery)

            const response: Nullable<AgentListResponse> = await getAgentList(
                filterTelSysID,
                filterPartitionID,
                false,
                0,
                isInitialOpenQuery ? INITIAL_MEMBER_OPTIONS_LIMIT : SEARCH_MEMBER_OPTIONS_LIMIT,
                isRangeQuery ? undefined : 'AgentNumber:ASC',
                isRangeQuery || isInitialOpenQuery ? undefined : searchQuery,
                isRangeQuery ? searchQuery : undefined,
                isRangeQuery ? undefined : AGENT_ADMIN_LIST_FIELDS
            )

            return (response?.Data?.Items ?? [])
                .filter((row: AgentListItem): boolean => isStringNotEmpty(row.AgentNumber ?? EMPTY_STRING))
                .map((row: AgentListItem) => ({
                    label: formatAgentOptionLabel(row),
                    value: row.AgentNumber ?? EMPTY_STRING,
                    original: row
                }))
        },
        [filterPartitionID, filterTelSysID]
    )

    const resolveValidIdentifiers = useCallback(
        async (
            pendingRanges: Nullable<RangeEntryWrapper[]>
        ): Promise<ReadonlyArray<MemberSearchOption<AgentListItem>>> => {
            const filterExpression: string = buildRangeFilterExpression(pendingRanges)

            if (!filterExpression) return []

            const response: Nullable<AgentListResponse> = await getAgentList(
                filterTelSysID,
                filterPartitionID,
                false,
                0,
                1000,
                undefined,
                undefined,
                filterExpression
            )

            return (response?.Data?.Items ?? [])
                .filter((row: AgentListItem): boolean => isStringNotEmpty(row.AgentNumber ?? EMPTY_STRING))
                .map(
                    (row: AgentListItem): MemberSearchOption<AgentListItem> => ({
                        value: row.AgentNumber ?? EMPTY_STRING,
                        label: formatAgentOptionLabel(row),
                        original: row
                    })
                )
        },
        [filterPartitionID, filterTelSysID]
    )

    return (
        <div className='agent-members-tab tab-area'>
            <MemberTable
                label={language.get(DeviceModal.DeviceModal_Tab_AgentMembers)}
                noRowsLabel={language.get(DeviceModal.DeviceModal_Members_NoAgentMembers)}
                searchAddButtonTitle={language.get(DeviceModal.DeviceModal_Members_AddHotkey)}
                removeButtonLabel={language.get(DeviceModal.DeviceModal_Members_Remove)}
                searchLabel={language.get(DeviceModal.DeviceModal_Members_Add)}
                rows={selectedRows}
                onRowsChange={onRowsChange}
                columns={columns}
                getIdentifier={(row: AgentMemberRow): string => row.AgentNumber}
                createRowFromOption={(option): AgentMemberRow => ({
                    key: option.value,
                    AgentNumber: option.value,
                    Description: option.original?.Description ?? EMPTY_STRING
                })}
                loadOptions={loadOptions}
                viewOnly={viewOnly}
                isRouterMode={isRouterMode}
                resolveValidIdentifiers={resolveValidIdentifiers}
            />
        </div>
    )
}
